import { useContext, useRef, useEffect, useState, useCallback, type FormEvent } from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { motion } from 'motion/react';
import { log } from '@/logger/logger.ts';
import { color, EL_COLORS } from '@/colors';
import { useWorkflowStore } from '@/state/workflow.state';
import { getHandleId } from '../../Nodes/Utils';
import { createNewNode } from '../useGraph';
import { StorybookFlowContext } from '../FlowContext';
import {
  topologicalLayout,
  getCanvasSummary,
  sanitizeInitialData,
  type WorkflowNodeSpec,
  type WorkflowEdgeSpec,
} from './ai-chat.utils';

function useFlowGraph() {
  const ctx = useContext(StorybookFlowContext);
  if (!ctx) throw new Error('Missing FlowContext.Provider in the tree');
  return ctx.graph;
}
import type { UIMessage } from 'ai';
import type { Node } from '@/types/node';
import type { useAIChat } from './useAIChat';

const logger = log.getLogger('AIChatContent');

const CANVAS_STATE_BLOCK = /^\[Current canvas state]\n[\s\S]*\[End canvas state]\n/;

function getMessageText(message: UIMessage): string {
  const raw = message.parts
    .filter((part) => part.type === 'text')
    .map((part) => part.text)
    .join('\n\n');
  if (message.role === 'user') return raw.replace(CANVAS_STATE_BLOCK, '');
  return raw;
}

function ThinkingIndicator() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
      <motion.div
        animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.15, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        style={{ display: 'flex' }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 14, color: color.White40_T }} />
      </motion.div>
      <Box sx={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2, ease: 'easeInOut' }}
            style={{
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: color.White40_T,
              display: 'inline-block',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

interface AIChatContentProps {
  chat: ReturnType<typeof useAIChat>;
  onChatStateChange?: (hasMessages: boolean, clearFn: () => void) => void;
}

export function AIChatContent({ chat, onChatStateChange }: AIChatContentProps) {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [input, setInput] = useState('');
  const [lastResult, setLastResult] = useState<string | null>(null);
  const flowGraph = useFlowGraph();

  const { messages, sendMessage, setMessages, status, processedToolCalls } = chat;
  const isActive = status === 'submitted' || status === 'streaming';

  const handleBuildWorkflow = useCallback(
    (args: { nodes: WorkflowNodeSpec[]; edges: WorkflowEdgeSpec[] }) => {
      const nodeTypes = useWorkflowStore.getState().nodeTypes;
      const existingNodes = flowGraph.store.getNodes();
      const positions = topologicalLayout(args.nodes, args.edges, existingNodes, nodeTypes);

      const refIdToNodeId = new Map<string, string>();
      const createdNodes: Node[] = [];
      const warnings: string[] = [];

      for (const spec of args.nodes) {
        const position = positions.get(spec.ref_id) ?? { x: 0, y: 0 };
        const nodeType = nodeTypes.find((nt) => nt.id === spec.node_type_id)?.type ?? '';
        const sanitizedData = sanitizeInitialData(nodeType, spec.initial_data);
        const node = createNewNode(spec.node_type_id, nodeTypes, position, sanitizedData);
        if (!node) {
          logger.warn(`Unknown node type: ${spec.node_type_id}`);
          warnings.push(`Skipped unknown node type: ${spec.node_type_id}`);
          continue;
        }
        if (spec.display_name) node.data.name = spec.display_name;
        refIdToNodeId.set(spec.ref_id, node.id);
        createdNodes.push(node);
      }

      const existingNodeIds = new Set(existingNodes.map((n) => n.id));
      const resolveNodeId = (refId: string): string | undefined =>
        refIdToNodeId.get(refId) ?? (existingNodeIds.has(refId) ? refId : undefined);

      const resolvedEdges: {
        sourceNodeId: string;
        targetNodeId: string;
        sourceHandle: string;
        targetHandle: string;
      }[] = [];
      for (const edgeSpec of args.edges) {
        const sourceNodeId = resolveNodeId(edgeSpec.source_ref_id);
        const targetNodeId = resolveNodeId(edgeSpec.target_ref_id);
        if (!sourceNodeId || !targetNodeId) {
          warnings.push(`Skipped edge: missing node ref ${edgeSpec.source_ref_id} → ${edgeSpec.target_ref_id}`);
          continue;
        }
        resolvedEdges.push({
          sourceNodeId,
          targetNodeId,
          sourceHandle: getHandleId(sourceNodeId, 'output', edgeSpec.source_output_key),
          targetHandle: getHandleId(targetNodeId, 'input', edgeSpec.target_input_key),
        });
      }

      // Validate edges:
      // 1. Handle keys must exist on the nodes
      // 2. Output type must match input type (image→image, text→text)
      // 3. Each input handle can only have one connection
      const allNodes = [...createdNodes, ...existingNodes];
      const usedTargetHandles = new Set<string>();
      const validResolvedEdges = resolvedEdges.filter((e) => {
        const sourceKey = e.sourceHandle.split('-output-')[1];
        const targetKey = e.targetHandle.split('-input-')[1];
        const sourceNode = allNodes.find((n) => n.id === e.sourceNodeId);
        const targetNode = allNodes.find((n) => n.id === e.targetNodeId);
        if (!sourceNode || !targetNode) return false;

        const srcHandles = sourceNode.data.handles?.output;
        const tgtHandles = targetNode.data.handles?.input;
        const srcOk = Array.isArray(srcHandles) ? srcHandles.includes(sourceKey) : sourceKey in (srcHandles ?? {});
        const tgtOk = Array.isArray(tgtHandles) ? tgtHandles.includes(targetKey) : targetKey in (tgtHandles ?? {});
        if (!srcOk || !tgtOk) {
          logger.warn(`Skipped edge: handle "${!srcOk ? sourceKey : targetKey}" not found`);
          warnings.push(`Skipped invalid connection (${sourceKey} → ${targetKey})`);
          return false;
        }

        // Type compatibility check
        if (!Array.isArray(srcHandles) && !Array.isArray(tgtHandles)) {
          const srcType = srcHandles?.[sourceKey]?.type;
          const tgtType = tgtHandles?.[targetKey]?.type;
          if (srcType && tgtType && srcType !== tgtType && srcType !== 'any' && tgtType !== 'any') {
            logger.warn(`Skipped edge: type mismatch ${srcType} → ${tgtType}`);
            warnings.push(`Skipped type mismatch (${srcType} → ${tgtType})`);
            return false;
          }
        }

        // One connection per input handle
        const targetHandleId = e.targetHandle;
        if (usedTargetHandles.has(targetHandleId)) {
          logger.warn(`Skipped edge: duplicate connection to ${targetKey}`);
          warnings.push(`Skipped duplicate connection to ${targetKey}`);
          return false;
        }
        usedTargetHandles.add(targetHandleId);

        return true;
      });

      // Add all nodes and edges as a single undo transaction
      flowGraph.edit(() => {
        const undoEntry = flowGraph.addNodes(createdNodes);
        const edges = validResolvedEdges.map((e) =>
          flowGraph.createEdge(e.sourceNodeId, e.targetNodeId, e.sourceHandle, e.targetHandle),
        );
        undoEntry.add(flowGraph.addEdges(edges));

        // Sync initial data values to WASM
        for (const node of createdNodes) {
          const data = node.data as unknown as Record<string, unknown>;
          if (node.type === 'promptV3' && typeof data.prompt === 'string') {
            undoEntry.add(flowGraph.wasmChange((wasm) => wasm.setPrompt(node.id, data.prompt as string)));
          } else if (node.type === 'string' && typeof (data.text ?? data.value) === 'string') {
            undoEntry.add(flowGraph.wasmChange((wasm) => wasm.setText(node.id, (data.text ?? data.value) as string)));
          } else if (
            node.type === 'muxv2' &&
            Array.isArray(data.list) &&
            (data.list as unknown[]).every((x) => typeof x === 'string')
          ) {
            const list = data.list as string[];
            const delimiter = typeof data.delimiter === 'string' ? data.delimiter : '*';
            undoEntry.add(flowGraph.wasmChange((wasm) => wasm.setListSelector(node.id, list, delimiter)));
            flowGraph.updateNodeOutputs(node.id);
          }
        }

        return undoEntry;
      });

      // Request viewport fit after nodes are added
      setTimeout(() => window.dispatchEvent(new CustomEvent('ai-workflow-changed')), 300);

      const resultParts = [`Created ${createdNodes.length} nodes with ${resolvedEdges.length} connections.`];
      if (warnings.length > 0) resultParts.push(`Warnings: ${warnings.join('; ')}`);
      setLastResult(resultParts.join(' '));
    },
    [flowGraph],
  );

  const handleModifyWorkflow = useCallback(
    (args: {
      remove_node_ids?: string[];
      remove_edges?: { source_node_id: string; target_node_id: string; target_input_key: string }[];
      add_nodes?: WorkflowNodeSpec[];
      add_edges?: WorkflowEdgeSpec[];
    }) => {
      const warnings: string[] = [];

      // Capture positions of nodes about to be removed so replacements can be placed
      // at the original location instead of being appended to the right of the canvas.
      let replacementAnchor: { x: number; y: number } | undefined;
      if (args.remove_node_ids?.length) {
        let minX = Infinity;
        let minY = Infinity;
        for (const id of args.remove_node_ids) {
          const n = flowGraph.store.getNodeNullable(id);
          if (!n) continue;
          if (n.position.x < minX) minX = n.position.x;
          if (n.position.y < minY) minY = n.position.y;
        }
        if (minX !== Infinity) replacementAnchor = { x: minX, y: minY };
      }

      flowGraph.edit(() => {
        const undoEntry = flowGraph.addNodes([]); // empty starter

        // 1. Remove edges
        if (args.remove_edges?.length) {
          const allEdges = flowGraph.store.getEdges();
          const edgeIdsToRemove = args.remove_edges
            .map((re) => {
              const edge = allEdges.find(
                (e) =>
                  e.source === re.source_node_id &&
                  e.target === re.target_node_id &&
                  e.targetHandle?.endsWith(`-input-${re.target_input_key}`),
              );
              if (!edge) {
                warnings.push(`Edge not found: ${re.source_node_id} → ${re.target_node_id}.${re.target_input_key}`);
              }
              return edge?.id;
            })
            .filter(Boolean) as string[];
          if (edgeIdsToRemove.length > 0) {
            undoEntry.add(flowGraph.removeEdges(edgeIdsToRemove));
          }
        }

        // 2. Remove nodes (removeNodes handles connected edges automatically)
        if (args.remove_node_ids?.length) {
          const validIds = args.remove_node_ids.filter((id) => {
            if (!flowGraph.store.getNodeNullable(id)) {
              warnings.push(`Node not found: ${id}`);
              return false;
            }
            return true;
          });
          if (validIds.length > 0) {
            undoEntry.add(flowGraph.removeNodes(validIds));
          }
        }

        // 3. Add new nodes
        const nodeTypes = useWorkflowStore.getState().nodeTypes;
        const existingNodes = flowGraph.store.getNodes();
        const refIdToNodeId = new Map<string, string>();
        const createdNodes: Node[] = [];

        if (args.add_nodes?.length) {
          const positions = topologicalLayout(
            args.add_nodes,
            args.add_edges ?? [],
            existingNodes,
            nodeTypes,
            replacementAnchor,
          );
          for (const spec of args.add_nodes) {
            const position = positions.get(spec.ref_id) ?? { x: 0, y: 0 };
            const nodeType = nodeTypes.find((nt) => nt.id === spec.node_type_id)?.type ?? '';
            const sanitizedData = sanitizeInitialData(nodeType, spec.initial_data);
            const node = createNewNode(spec.node_type_id, nodeTypes, position, sanitizedData);
            if (!node) {
              warnings.push(`Unknown node type: ${spec.node_type_id}`);
              continue;
            }
            if (spec.display_name) node.data.name = spec.display_name;
            refIdToNodeId.set(spec.ref_id, node.id);
            createdNodes.push(node);
          }
          undoEntry.add(flowGraph.addNodes(createdNodes));
        }

        // 4. Sync initial data to WASM for newly created nodes. Must run
        // regardless of whether edges are being added so prompt/text/list
        // values are committed even when add_edges is empty.
        for (const node of createdNodes) {
          const data = node.data as unknown as Record<string, unknown>;
          if (node.type === 'promptV3' && typeof data.prompt === 'string') {
            undoEntry.add(flowGraph.wasmChange((wasm) => wasm.setPrompt(node.id, data.prompt as string)));
          } else if (node.type === 'string' && typeof (data.text ?? data.value) === 'string') {
            undoEntry.add(flowGraph.wasmChange((wasm) => wasm.setText(node.id, (data.text ?? data.value) as string)));
          } else if (
            node.type === 'muxv2' &&
            Array.isArray(data.list) &&
            (data.list as unknown[]).every((x) => typeof x === 'string')
          ) {
            const list = data.list as string[];
            const delimiter = typeof data.delimiter === 'string' ? data.delimiter : '*';
            undoEntry.add(flowGraph.wasmChange((wasm) => wasm.setListSelector(node.id, list, delimiter)));
            flowGraph.updateNodeOutputs(node.id);
          }
        }

        // 5. Add new edges
        if (args.add_edges?.length) {
          const existingNodeIds = new Set(flowGraph.store.getNodes().map((n) => n.id));
          const resolveNodeId = (refId: string): string | undefined =>
            refIdToNodeId.get(refId) ?? (existingNodeIds.has(refId) ? refId : undefined);

          const allNodes = [...createdNodes, ...flowGraph.store.getNodes()];
          const edges: ReturnType<typeof flowGraph.createEdge>[] = [];
          for (const edgeSpec of args.add_edges) {
            const sourceNodeId = resolveNodeId(edgeSpec.source_ref_id);
            const targetNodeId = resolveNodeId(edgeSpec.target_ref_id);
            if (!sourceNodeId || !targetNodeId) {
              warnings.push(`Edge ref not found: ${edgeSpec.source_ref_id} → ${edgeSpec.target_ref_id}`);
              continue;
            }
            const sourceHandle = getHandleId(sourceNodeId, 'output', edgeSpec.source_output_key);
            const targetHandle = getHandleId(targetNodeId, 'input', edgeSpec.target_input_key);
            // Validate handles exist
            const sourceNode = allNodes.find((n) => n.id === sourceNodeId);
            const targetNode = allNodes.find((n) => n.id === targetNodeId);
            const srcHandles = sourceNode?.data.handles?.output;
            const tgtHandles = targetNode?.data.handles?.input;
            const srcKey = edgeSpec.source_output_key;
            const tgtKey = edgeSpec.target_input_key;
            const srcOk = Array.isArray(srcHandles) ? srcHandles.includes(srcKey) : srcKey in (srcHandles ?? {});
            const tgtOk = Array.isArray(tgtHandles) ? tgtHandles.includes(tgtKey) : tgtKey in (tgtHandles ?? {});
            if (!srcOk || !tgtOk) {
              warnings.push(`Invalid handle: ${srcKey} → ${tgtKey}`);
              continue;
            }
            try {
              edges.push(flowGraph.createEdge(sourceNodeId, targetNodeId, sourceHandle, targetHandle));
            } catch {
              warnings.push(`Edge creation failed: ${srcKey} → ${tgtKey}`);
            }
          }
          undoEntry.add(flowGraph.addEdges(edges));
        }

        return undoEntry;
      });

      const parts: string[] = [];
      if (args.remove_node_ids?.length) parts.push(`Removed ${args.remove_node_ids.length} nodes`);
      if (args.remove_edges?.length) parts.push(`Removed ${args.remove_edges.length} edges`);
      if (args.add_nodes?.length) parts.push(`Added ${args.add_nodes.length} nodes`);
      if (args.add_edges?.length) parts.push(`Added ${args.add_edges.length} edges`);
      if (warnings.length > 0) parts.push(`Warnings: ${warnings.join('; ')}`);
      setLastResult(parts.join('. ') || 'Workflow modified.');

      // Request viewport fit after modification
      setTimeout(() => window.dispatchEvent(new CustomEvent('ai-workflow-changed')), 300);
    },
    [flowGraph],
  );

  const getCanvasContext = useCallback(() => {
    return getCanvasSummary(flowGraph.store.getNodes(), flowGraph.store.getEdges());
  }, [flowGraph]);

  // Process tool calls from streamed messages
  useEffect(() => {
    for (const message of messages) {
      if (message.role !== 'assistant') continue;
      for (const part of message.parts) {
        const p = part as any;
        if (p.type?.startsWith('tool-') && p.toolCallId && p.state === 'output-available') {
          const toolName = p.type.replace(/^tool-/, '');
          if (toolName !== 'build_workflow' && toolName !== 'modify_workflow') continue;
          if (processedToolCalls.current.has(p.toolCallId)) continue;
          processedToolCalls.current.add(p.toolCallId);
          if (p.input) {
            try {
              if (toolName === 'build_workflow') {
                handleBuildWorkflow(p.input);
              } else {
                handleModifyWorkflow(p.input);
              }
            } catch (err) {
              logger.error(`Error in ${toolName}:`, err);
              setLastResult(`Error: ${err instanceof Error ? err.message : String(err)}`);
            }
          }
        }
      }
    }
  }, [messages, handleBuildWorkflow, handleModifyWorkflow, processedToolCalls]);

  const handleClear = useCallback(() => {
    setMessages([]);
    setLastResult(null);
    setInput('');
    processedToolCalls.current.clear();
  }, [setMessages, processedToolCalls]);

  // Notify parent of chat state for tab bar clear button
  useEffect(() => {
    onChatStateChange?.(messages.length > 0, handleClear);
  }, [messages.length, handleClear, onChatStateChange]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const text = input.trim();
    if (!text || isActive) return;
    setInput('');
    setLastResult(null);
    const canvasState = getCanvasContext();
    const contextPrefix = `[Current canvas state]\n${canvasState}\n[End canvas state]\n`;
    void sendMessage({ text: `${contextPrefix}${text}` });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Only stop propagation for keys that would interfere with ReactFlow (e.g. Delete, Backspace).
    // Do NOT stop modifier keys (Cmd, Ctrl, Shift, Alt) — ReactFlow needs them for zoom/pan.
    if (!e.metaKey && !e.ctrlKey && !e.altKey) {
      e.stopPropagation();
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      formRef.current?.requestSubmit();
    }
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, lastResult]);

  const hasMessages = messages.length > 0 || isActive;

  const inputForm = (
    <Box
      ref={formRef}
      component="form"
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 1,
        ...(hasMessages ? { borderTop: `1px solid ${EL_COLORS.BoxBorder}` } : {}),
        flexShrink: 0,
      }}
    >
      <TextField
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Describe a workflow..."
        size="small"
        fullWidth
        multiline
        maxRows={16}
        autoComplete="off"
        sx={{
          '& .MuiInputBase-root': {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '12px',
            color: color.White100,
            backgroundColor: 'rgba(255,255,255,0.06)',
          },
          '& .MuiInputBase-input': { padding: '6px 8px' },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: EL_COLORS.BoxBorder,
          },
        }}
      />
      <IconButton type="submit" disabled={isActive || !input.trim()} size="small" sx={{ color: color.White64_T }}>
        <SendIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );

  return (
    <>
      {/* No messages: input at top */}
      {!hasMessages && inputForm}

      {/* Conversation: messages + input at bottom */}
      {hasMessages && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Box
            ref={messagesContainerRef}
            sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}
            className="wea-no-scrollbar"
          >
            {messages.map((message) => {
              const text = getMessageText(message);
              if (!text) return null;
              const isUser = message.role === 'user';
              return (
                <Box
                  key={message.id}
                  sx={{ mb: 1, display: 'flex', justifyContent: isUser ? 'flex-start' : 'flex-end' }}
                >
                  <Typography
                    variant="body-xs-rg"
                    sx={{
                      color: isUser ? color.White100 : color.White80_T,
                      backgroundColor: isUser ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      display: 'inline-block',
                      fontSize: '12px',
                      lineHeight: 1.5,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: '16px',
                      maxWidth: '85%',
                    }}
                  >
                    {text}
                  </Typography>
                </Box>
              );
            })}
            {isActive && <ThinkingIndicator />}
            {lastResult && (
              <Box sx={{ p: 1, borderRadius: 1, backgroundColor: 'rgba(100,200,100,0.08)' }}>
                <Typography variant="body-xs-rg" sx={{ color: color.White64_T, fontSize: '12px' }}>
                  {lastResult}
                </Typography>
              </Box>
            )}
          </Box>

          {inputForm}
        </Box>
      )}
    </>
  );
}
